import React, { useEffect, useState } from "react";
import { DatePicker, Button, Typography, Modal, Row, Col, Space, Table, Spin } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/en";
import { getErrorScripts } from "./assistant-service";
import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";
import CodeEditor from "../common/code-editor";

dayjs.locale("en");

const ErrorScriptView = () => {
  const { apiCall } = useApiCall();
  const { errorToast, successToast } = useToast();

  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(dayjs().format("YYYY-MM-DD"));
  const [selectedDayValue, setSelectedDayValue] = useState(dayjs());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentScript, setCurrentScript] = useState(null);

  useEffect(() => {
    if (selectedDay) {
      fetchErrorScripts();
    }
  }, [selectedDay]);

  const fetchErrorScripts = async () => {
    try {
      setLoading(true);
      // Use YYYY-MM-DD format for API
      const apiDay = selectedDay;
      const data = await getErrorScripts(apiCall, apiDay);

      if (data && data.scripts) {
        // Add id for DataGrid and process scripts
        const processedScripts = data.scripts.map((script, index) => ({
          id: script.scriptId || index,
          scriptId: script.scriptId,
          script: script.script,
          day: script.day,
        }));
        setScripts(processedScripts);
      } else {
        setScripts([]);
      }
      setLoading(false);
    } catch (error) {
      errorToast("Failed to fetch error scripts");
      setLoading(false);
    }
  };

  const handleRowClick = params => {
    setCurrentScript(params.row);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentScript(null);
  };

  const columns = [
    {
      title: "Script ID",
      dataIndex: "scriptId",
      key: "scriptId",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Day",
      dataIndex: "day",
      key: "day",
      width: 120,
    },
    {
      title: "Script Preview",
      dataIndex: "script",
      key: "script",
      ellipsis: true,
      render: (text) => text ? `${text.substring(0, 100)}...` : "",
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleRowClick({ row: record })}
        >
          View Script
        </Button>
      ),
    },
  ];

  return (
    <div style={{ flexGrow: 1, padding: 16 }}>
      <Row gutter={16}>
        <Col span={24}>
          <Space style={{ marginBottom: 16 }}>
            <DatePicker
              value={selectedDayValue}
              onChange={(date) => {
                setSelectedDayValue(date);
                setSelectedDay(date ? date.format("YYYY-MM-DD") : "");
              }}
              format="YYYY-MM-DD"
              style={{ width: 200 }}
            />
            <Button type="primary" onClick={fetchErrorScripts}>
              Refresh
            </Button>
          </Space>
        </Col>

        <Col span={24}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <LoadingSpinner />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={scripts}
              rowKey="id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
              }}
              onRow={(record) => ({
                onClick: () => handleRowClick({ row: record }),
                style: { cursor: "pointer" },
              })}
            />
          )}
        </Col>
      </Row>

      {/* Script Modal */}
      <Modal
        open={dialogOpen}
        onCancel={handleCloseDialog}
        width="90%"
        style={{ top: 20 }}
        footer={[
          <Button key="close" onClick={handleCloseDialog}>
            Close
          </Button>,
        ]}
        closeIcon={<CloseOutlined />}
      >
        <div style={{ marginTop: 16 }}>
          <Typography.Text strong>Day: {currentScript?.day}</Typography.Text>
          <div style={{ marginTop: 16 }}>
            <CodeEditor
              value={currentScript?.script || ""}
              editable={false}
              height="600px"
              language="javascript"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ErrorScriptView;
